import { useState, useEffect, useCallback } from 'react'
import { Activity, TrendingUp, AlertCircle, CheckCircle, XCircle, Play, Loader2 } from 'lucide-react'

const API_BASE = '/api'

interface WorkflowNode {
  workflow_name: string
  node_name: string
  eval_count: number
}

interface DimensionStats {
  count: number
  avg: number
  min: number
  max: number
  fail_count: number
}

interface NodeStats {
  workflow_name: string
  node_name: string
  total: number
  avg_score: number | null
  pass_count: number
  fail_count: number
  pass_rate: number | null
  dimensions: Record<string, DimensionStats>
}

interface EvalSummary {
  id: string
  session_id: string
  turn_id: string
  workflow_name: string
  node_name: string
  overall_score: number | null
  passed: string
  eval_model: string
  evaluated_at: string
  scores: Record<string, number>
}

interface EvalDetail {
  id: string
  session_id: string
  turn_id: string
  workflow_name: string
  node_name: string
  scores: Record<string, number>
  overall_score: number | null
  passed: string
  eval_model: string
  eval_reasoning: string | null
  context_snapshot: Record<string, unknown> | null
  evaluated_at: string
  turn_content: string | null
  user_message: string | null
}

export function EvalsPage() {
  const [nodes, setNodes] = useState<WorkflowNode[]>([])
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null)
  const [stats, setStats] = useState<NodeStats | null>(null)
  const [evals, setEvals] = useState<EvalSummary[]>([])
  const [selectedEval, setSelectedEval] = useState<EvalDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pass' | 'fail'>('all')
  const [runningEvals, setRunningEvals] = useState(false)
  const [pendingCount, setPendingCount] = useState<number | null>(null)
  const [runResult, setRunResult] = useState<{ evaluated: number; errors: number } | null>(null)

  // Fetch available nodes
  useEffect(() => {
    async function fetchNodes() {
      try {
        const res = await fetch(`${API_BASE}/evals/nodes`)
        if (res.ok) {
          const data = await res.json()
          setNodes(data)
          if (data.length > 0) {
            setSelectedNode(data[0])
          }
        }
      } catch (e) {
        console.error('Failed to fetch nodes:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchNodes()
  }, [])

  // Fetch pending count for selected node
  const fetchPendingCount = useCallback(async () => {
    if (!selectedNode) return
    try {
      const res = await fetch(
        `${API_BASE}/evals/pending/${selectedNode.workflow_name}/${selectedNode.node_name}`
      )
      if (res.ok) {
        const data = await res.json()
        setPendingCount(data.pending_count)
      }
    } catch (e) {
      console.error('Failed to fetch pending count:', e)
    }
  }, [selectedNode])

  useEffect(() => {
    fetchPendingCount()
  }, [fetchPendingCount])

  // Run evals for selected node
  const handleRunEvals = async () => {
    if (!selectedNode || runningEvals) return
    setRunningEvals(true)
    setRunResult(null)
    try {
      const res = await fetch(`${API_BASE}/evals/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_name: selectedNode.node_name,
          limit: 20,
        }),
      })
      if (res.ok) {
        const data = await res.json()
        setRunResult({ evaluated: data.evaluated, errors: data.errors })
        // Refresh data after running
        fetchPendingCount()
        // Trigger a re-fetch of stats and evals
        setSelectedNode({ ...selectedNode })
      }
    } catch (e) {
      console.error('Failed to run evals:', e)
    } finally {
      setRunningEvals(false)
    }
  }

  // Fetch stats and evals for selected node
  useEffect(() => {
    if (!selectedNode) return

    async function fetchData() {
      if (!selectedNode) return
      setLoading(true)
      try {
        const { workflow_name, node_name } = selectedNode
        const [statsRes, evalsRes] = await Promise.all([
          fetch(`${API_BASE}/evals/stats/${workflow_name}/${node_name}`),
          fetch(`${API_BASE}/evals/list/${workflow_name}/${node_name}?limit=100${filter !== 'all' ? `&passed=${filter}` : ''}`),
        ])

        if (statsRes.ok) {
          setStats(await statsRes.json())
        }
        if (evalsRes.ok) {
          setEvals(await evalsRes.json())
        }
      } catch (e) {
        console.error('Failed to fetch data:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [selectedNode, filter])

  // Fetch eval detail
  const handleSelectEval = async (evalId: string) => {
    try {
      const res = await fetch(`${API_BASE}/evals/detail/${evalId}`)
      if (res.ok) {
        setSelectedEval(await res.json())
      }
    } catch (e) {
      console.error('Failed to fetch eval detail:', e)
    }
  }

  if (loading && nodes.length === 0) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-6 h-6 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground font-light">
            Loading evals...
          </p>
        </div>
      </div>
    )
  }

  if (nodes.length === 0) {
    return (
      <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-6">
            <Activity className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-light tracking-tight text-foreground mb-4">
            No Evaluations Yet
          </h1>
          <p className="text-muted-foreground font-light mb-6">
            Click below to run evaluations on your workflow nodes.
          </p>
          <button
            onClick={async () => {
              setRunningEvals(true)
              try {
                const res = await fetch(`${API_BASE}/evals/run`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ node_name: 'gatekeeper', limit: 20 }),
                })
                if (res.ok) {
                  // Refresh the page to show new evals
                  window.location.reload()
                }
              } catch (e) {
                console.error('Failed to run evals:', e)
              } finally {
                setRunningEvals(false)
              }
            }}
            disabled={runningEvals}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-light bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mx-auto"
          >
            {runningEvals ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Running Evaluations...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Evaluations
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 h-14 border-b border-border flex items-center justify-between px-6 md:px-8">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-foreground" strokeWidth={1.5} />
          <h1 className="text-lg font-light tracking-tight text-foreground">
            Node Evaluations
          </h1>
        </div>
        <div className="flex items-center gap-4">
          {runResult && (
            <span className="text-xs text-muted-foreground">
              Last run: {runResult.evaluated} evaluated
              {runResult.errors > 0 && `, ${runResult.errors} errors`}
            </span>
          )}
          {pendingCount !== null && pendingCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {pendingCount} pending
            </span>
          )}
          <button
            onClick={handleRunEvals}
            disabled={runningEvals || !selectedNode}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-light bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {runningEvals ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Evals
              </>
            )}
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-6">
            {/* Node selector */}
            <div className="mb-8">
              <label className="text-[10px] font-light tracking-[0.08em] uppercase text-muted-foreground mb-2 block">
                Select Node
              </label>
              <div className="flex flex-wrap gap-2">
                {nodes.map((node) => (
                  <button
                    key={`${node.workflow_name}/${node.node_name}`}
                    onClick={() => {
                      setSelectedNode(node)
                      setSelectedEval(null)
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-light transition-colors ${
                      selectedNode?.node_name === node.node_name
                        ? 'bg-foreground text-background'
                        : 'border border-border hover:bg-muted'
                    }`}
                  >
                    {node.workflow_name}/{node.node_name}
                    <span className="ml-2 text-xs opacity-60">({node.eval_count})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Stats cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard
                  label="Total Evals"
                  value={stats.total}
                  icon={<Activity className="w-4 h-4" />}
                />
                <StatCard
                  label="Avg Score"
                  value={stats.avg_score?.toFixed(2) || 'N/A'}
                  subtext="out of 4.00"
                  icon={<TrendingUp className="w-4 h-4" />}
                />
                <StatCard
                  label="Pass Rate"
                  value={stats.pass_rate ? `${stats.pass_rate.toFixed(0)}%` : 'N/A'}
                  subtext={`${stats.pass_count} passed`}
                  icon={<CheckCircle className="w-4 h-4 text-green-600" />}
                />
                <StatCard
                  label="Failures"
                  value={stats.fail_count}
                  subtext="mandatory dimension = 1"
                  icon={<XCircle className="w-4 h-4 text-red-500" />}
                />
              </div>
            )}

            {/* Dimension breakdown */}
            {stats && Object.keys(stats.dimensions).length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-light text-foreground mb-4">Dimension Scores</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(stats.dimensions)
                    .sort(([, a], [, b]) => a.avg - b.avg)
                    .map(([dim, dstats]) => (
                      <DimensionRow key={dim} name={dim} stats={dstats} />
                    ))}
                </div>
              </div>
            )}

            {/* Filter tabs */}
            <div className="flex items-center gap-4 mb-4 border-b border-border">
              {(['all', 'pass', 'fail'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`pb-2 text-sm font-light transition-colors relative ${
                    filter === f
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'pass' ? 'Passed' : 'Failed'}
                  {filter === f && (
                    <span className="absolute bottom-0 left-0 right-0 h-px bg-foreground" />
                  )}
                </button>
              ))}
            </div>

            {/* Evals list */}
            <div className="space-y-2">
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-5 h-5 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
                </div>
              ) : evals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground font-light">
                  No evaluations found
                </div>
              ) : (
                evals.map((evalItem) => (
                  <EvalRow
                    key={evalItem.id}
                    eval={evalItem}
                    isSelected={selectedEval?.id === evalItem.id}
                    onClick={() => handleSelectEval(evalItem.id)}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Detail panel */}
        {selectedEval && (
          <div className="w-[400px] flex-shrink-0 border-l border-border overflow-y-auto bg-muted/30">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-light text-foreground">Eval Detail</h2>
                <button
                  onClick={() => setSelectedEval(null)}
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                >
                  Close
                </button>
              </div>

              {/* Status badge */}
              <div className="mb-6">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-light ${
                    selectedEval.passed === 'pass'
                      ? 'bg-green-500/10 text-green-700'
                      : selectedEval.passed === 'fail'
                      ? 'bg-red-500/10 text-red-700'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {selectedEval.passed === 'pass' ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : selectedEval.passed === 'fail' ? (
                    <XCircle className="w-3 h-3" />
                  ) : (
                    <AlertCircle className="w-3 h-3" />
                  )}
                  {selectedEval.passed.toUpperCase()}
                </span>
                <span className="ml-3 text-sm text-muted-foreground">
                  Score: {selectedEval.overall_score?.toFixed(2) || 'N/A'}
                </span>
              </div>

              {/* Scores breakdown */}
              <div className="mb-6">
                <h3 className="text-[10px] font-light tracking-[0.08em] uppercase text-muted-foreground mb-3">
                  Dimension Scores
                </h3>
                <div className="space-y-2">
                  {Object.entries(selectedEval.scores).map(([dim, score]) => (
                    <div key={dim} className="flex items-center justify-between">
                      <span className="text-sm font-light text-foreground">
                        {dim.replace(/_/g, ' ')}
                      </span>
                      <span
                        className={`text-sm font-light ${
                          score === 1
                            ? 'text-red-600'
                            : score === 4
                            ? 'text-green-600'
                            : 'text-foreground'
                        }`}
                      >
                        {score ?? 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* User message */}
              {selectedEval.user_message && (
                <div className="mb-6">
                  <h3 className="text-[10px] font-light tracking-[0.08em] uppercase text-muted-foreground mb-3">
                    User Message
                  </h3>
                  <p className="text-sm font-light text-foreground bg-background p-3 rounded-md border border-border">
                    {selectedEval.user_message}
                  </p>
                </div>
              )}

              {/* Turn content */}
              {selectedEval.turn_content && (
                <div className="mb-6">
                  <h3 className="text-[10px] font-light tracking-[0.08em] uppercase text-muted-foreground mb-3">
                    AI Response
                  </h3>
                  <p className="text-sm font-light text-foreground bg-background p-3 rounded-md border border-border whitespace-pre-wrap">
                    {selectedEval.turn_content.slice(0, 500)}
                    {selectedEval.turn_content.length > 500 && '...'}
                  </p>
                </div>
              )}

              {/* Reasoning */}
              {selectedEval.eval_reasoning && (
                <div className="mb-6">
                  <h3 className="text-[10px] font-light tracking-[0.08em] uppercase text-muted-foreground mb-3">
                    Eval Reasoning
                  </h3>
                  <pre className="text-xs font-light text-muted-foreground bg-background p-3 rounded-md border border-border whitespace-pre-wrap overflow-x-auto">
                    {selectedEval.eval_reasoning}
                  </pre>
                </div>
              )}

              {/* Metadata */}
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Model: {selectedEval.eval_model}</p>
                <p>Evaluated: {new Date(selectedEval.evaluated_at).toLocaleString()}</p>
                <p className="font-mono text-[10px] break-all">Turn: {selectedEval.turn_id}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  subtext,
  icon,
}: {
  label: string
  value: string | number
  subtext?: string
  icon: React.ReactNode
}) {
  return (
    <div className="p-4 rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        {icon}
        <span className="text-[10px] font-light tracking-[0.08em] uppercase">{label}</span>
      </div>
      <p className="text-2xl font-light text-foreground">{value}</p>
      {subtext && (
        <p className="text-xs text-muted-foreground font-light mt-1">{subtext}</p>
      )}
    </div>
  )
}

function DimensionRow({ name, stats }: { name: string; stats: DimensionStats }) {
  const percentage = ((stats.avg - 1) / 3) * 100 // 1-4 scale to 0-100
  const isMandatory = ['extraction_depth', 'query_precision', 'uncertainty_honesty'].includes(name)

  return (
    <div className="p-3 rounded-md border border-border bg-card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-light text-foreground">
          {name.replace(/_/g, ' ')}
          {isMandatory && (
            <span className="ml-1.5 text-[10px] text-muted-foreground">(mandatory)</span>
          )}
        </span>
        <span className="text-sm font-light text-foreground">{stats.avg.toFixed(2)}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${
            stats.avg < 2 ? 'bg-red-500' : stats.avg < 3 ? 'bg-amber-500' : 'bg-green-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground">
        <span>
          range: {stats.min}–{stats.max}
        </span>
        {stats.fail_count > 0 && (
          <span className="text-red-500">{stats.fail_count} at score 1</span>
        )}
      </div>
    </div>
  )
}

function EvalRow({
  eval: evalItem,
  isSelected,
  onClick,
}: {
  eval: EvalSummary
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-md border transition-colors ${
        isSelected
          ? 'border-foreground/30 bg-muted/50'
          : 'border-border hover:border-foreground/20 hover:bg-muted/30'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              evalItem.passed === 'pass'
                ? 'bg-green-500'
                : evalItem.passed === 'fail'
                ? 'bg-red-500'
                : 'bg-muted-foreground'
            }`}
          />
          <span className="text-sm font-light text-foreground">
            {evalItem.overall_score?.toFixed(2) || 'N/A'}
          </span>
          <span className="text-xs text-muted-foreground font-mono">
            {evalItem.turn_id.slice(0, 8)}...
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(evalItem.evaluated_at).toLocaleDateString()}
        </span>
      </div>
    </button>
  )
}
